import { useState } from 'react';
import { ChevronDown, ChevronRight, Search, BookOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { EndpointInfo } from '@/types/openapi';

interface SidebarProps {
  tags: Array<{ name: string; description?: string }>;
  endpointsByTag: Record<string, EndpointInfo[]>;
  selectedGroup: string | null;
  selectedEndpoint: EndpointInfo | null;
  selectedGettingStarted: boolean;
  onSelectGroup: (group: string) => void;
  onSelectEndpoint: (endpoint: EndpointInfo) => void;
  onSelectGettingStarted: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const methodColors: Record<string, string> = {
  get: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  post: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  put: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  delete: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  patch: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

export function Sidebar({
  tags,
  endpointsByTag,
  selectedGroup,
  selectedEndpoint,
  selectedGettingStarted,
  onSelectGroup,
  onSelectEndpoint,
  onSelectGettingStarted,
  searchQuery,
  onSearchChange,
}: SidebarProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(tags.map(t => t.name)));

  const toggleGroup = (tagName: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagName)) {
        newSet.delete(tagName);
      } else {
        newSet.add(tagName);
      }
      return newSet;
    });
    onSelectGroup(tagName);
  };

  const getTagDisplayName = (tagName: string): string => {
    // Convert tag names like "Instances" to display names
    const tag = tags.find(t => t.name === tagName);
    return tag?.name || tagName;
  };

  const getEndpointCount = (tagName: string): number => {
    return endpointsByTag[tagName]?.length || 0;
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search endpoints..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tags and Endpoints */}
      <div className="space-y-1">
        {/* Getting Started Menu Item */}
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-start h-auto py-2 px-2',
            selectedGettingStarted && 'bg-secondary'
          )}
          onClick={onSelectGettingStarted}
        >
          <div className="flex items-center gap-2 w-full">
            <BookOpen className="h-4 w-4 shrink-0" />
            <span className="font-medium">Getting Started</span>
          </div>
        </Button>

        {/* Divider */}
        <div className="border-t my-2" />

        {Object.keys(endpointsByTag).map((tagName) => (
          <div key={tagName} className="space-y-1">
            {/* Tag Header */}
            <Button
              variant="ghost"
              className={cn(
                'w-full justify-start h-auto py-2 px-2',
                selectedGroup === tagName && 'bg-secondary'
              )}
              onClick={() => toggleGroup(tagName)}
            >
              <div className="flex items-center gap-2 w-full">
                {expandedGroups.has(tagName) ? (
                  <ChevronDown className="h-4 w-4 shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0" />
                )}
                <span className="font-medium">{getTagDisplayName(tagName)}</span>
                <Badge variant="secondary" className="ml-auto text-xs">
                  {getEndpointCount(tagName)}
                </Badge>
              </div>
            </Button>

            {/* Endpoints List */}
            {expandedGroups.has(tagName) && endpointsByTag[tagName] && (
              <div className="ml-4 space-y-0.5">
                {endpointsByTag[tagName].map((endpoint) => (
                  <Button
                    key={`${endpoint.method}-${endpoint.path}`}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'w-full justify-start h-auto py-1.5 px-2 text-sm',
                      selectedEndpoint?.path === endpoint.path &&
                        selectedEndpoint?.method === endpoint.method &&
                        'bg-secondary'
                    )}
                    onClick={() => onSelectEndpoint(endpoint)}
                  >
                    <div className="flex items-center gap-2 w-full overflow-hidden">
                      <Badge
                        className={cn(
                          'text-xs font-mono shrink-0',
                          methodColors[endpoint.method]
                        )}
                      >
                        {endpoint.method.toUpperCase()}
                      </Badge>
                      <span className="truncate text-left font-mono">
                        {endpoint.path}
                      </span>
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* No Results */}
      {searchQuery && Object.keys(endpointsByTag).length === 0 && (
        <div className="text-center py-4 text-muted-foreground">
          <p className="text-sm">No endpoints found matching "{searchQuery}"</p>
        </div>
      )}
    </div>
  );
}